// Search results display with highlighting and keyboard navigation
// Shows matching entries with snippets and matched field indicators

import React, { useState, useEffect, useRef } from 'react';
import { FileText, Image, Smile } from 'lucide-react';
import { highlightMatches } from '../../utils/search/searchEngine';

/**
 * HighlightedText - Renders text with highlighted matches
 */
function HighlightedText({ text, query }) {
  const segments = highlightMatches(text, query);

  return (
    <span>
      {segments.map((segment, index) => (
        segment.highlight ? (
          <mark
            key={`hl-${index}`}
            className="bg-[#ffb85e]/20 text-white rounded px-0.5"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={`txt-${index}`}>{segment.text}</span>
        )
      ))}
    </span>
  );
}

/**
 * SearchResultCard - Individual result card
 */
function SearchResultCard({ result, query, isSelected, onClick }) {
  const { entry, snippet, matchedFields } = result;
  const cardRef = useRef(null);

  // Scroll into view when selected
  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [isSelected]);

  // Format date
  const formatDate = (timestamp) => {
    let date;
    if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      return 'Unknown';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - entryDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Mood color mapping (instead of emojis)
  const moodColors = {
    happy: 'bg-yellow-400',
    sad: 'bg-blue-400',
    calm: 'bg-green-400',
    anxious: 'bg-purple-400',
    excited: 'bg-pink-400',
    angry: 'bg-red-400',
    grateful: 'bg-amber-400',
    neutral: 'bg-gray-400'
  };

  // Get display text (prefer content over extractedText)
  const displayText = entry.content || entry.extractedText || '';
  const previewText = snippet || displayText.substring(0, 120) + (displayText.length > 120 ? '...' : '');

  return (
    <button
      ref={cardRef}
      onClick={onClick}
      className={`
        w-full text-left p-4 rounded-xl transition-all
        ${isSelected
          ? 'bg-white/10 ring-1 ring-white/20'
          : 'bg-white/[0.03] hover:bg-white/[0.06]'
        }
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-1.5">
        {/* Mood indicator */}
        {entry.mood && (
          <span
            className={`w-2 h-2 rounded-full ${moodColors[entry.mood] || 'bg-gray-400'} flex-shrink-0`}
            title={entry.mood}
          />
        )}

        {/* Date */}
        <span className="text-xs text-white/25">
          {formatDate(entry.createdAt)}
        </span>

        {/* Matched Fields - Small icons */}
        {matchedFields && matchedFields.length > 0 && (
          <div className="flex gap-1 ml-auto">
            {matchedFields.includes('content') && (
              <FileText size={10} className="text-white/20" title="Text match" />
            )}
            {matchedFields.includes('extracted') && (
              <Image size={10} className="text-white/20" title="Image match" />
            )}
            {matchedFields.includes('mood') && (
              <Smile size={10} className="text-white/20" title="Mood match" />
            )}
          </div>
        )}
      </div>

      {/* Preview Text with Highlighting */}
      <div className="text-sm text-white/70 line-clamp-2 font-light">
        <HighlightedText text={previewText} query={query} />
      </div>
    </button>
  );
}

/**
 * SearchResults - Main results component
 * Displays search results with keyboard navigation
 */
export default function SearchResults({ results, query, onSelectEntry }) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelectEntry(results[selectedIndex].entry);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [results, selectedIndex, onSelectEntry]);

  if (results.length === 0) {
    return null;
  }

  return (
    <div className="px-5 py-3">
      {/* Results List */}
      <div className="space-y-1.5">
        {results.map((result, index) => (
          <SearchResultCard
            key={result.entry.id || index}
            result={result}
            query={query}
            isSelected={index === selectedIndex}
            onClick={() => onSelectEntry(result.entry)}
          />
        ))}
      </div>

      {/* Load More hint */}
      {results.length >= 50 && (
        <div className="mt-3 text-center">
          <p className="text-xs text-white/20">
            Showing 50 results. Refine search for more.
          </p>
        </div>
      )}
    </div>
  );
}
